'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  decodeToCanvas,
  encodeCanvasInSoftware,
  SOFTWARE_DECODED,
  UNDECODABLE_MESSAGE,
} from '@/lib/software-image-decode';

type OutputFormat = 'png' | 'jpeg' | 'webp' | 'bmp' | 'tiff';
type ConversionRoute = {
  id: string;
  label: string;
  output: OutputFormat;
  hint: string;
};
type SourceMeta = {
  width: number;
  height: number;
};

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
  { value: 'bmp', label: 'BMP', mimeType: 'image/bmp', extension: 'bmp', qualityEnabled: false },
  {
    value: 'tiff',
    label: 'TIFF',
    mimeType: 'image/tiff',
    extension: 'tiff',
    qualityEnabled: false,
  },
];

const pngRouteOptions: ConversionRoute[] = [
  {
    id: 'png-jpg',
    label: 'PNG → JPG',
    output: 'jpeg',
    hint: 'Good when you want a lighter photo-friendly export.',
  },
  {
    id: 'png-webp',
    label: 'PNG → WebP',
    output: 'webp',
    hint: 'Useful for smaller web-ready files.',
  },
  {
    id: 'png-bmp',
    label: 'PNG → BMP',
    output: 'bmp',
    hint: 'Useful for older bitmap-friendly workflows.',
  },
  {
    id: 'jpg-png',
    label: 'JPG → PNG',
    output: 'png',
    hint: 'Better for screenshots, graphics, and cleaner edges.',
  },
  {
    id: 'webp-png',
    label: 'WebP → PNG',
    output: 'png',
    hint: 'Helpful when another app does not accept WebP well.',
  },
  {
    id: 'gif-png',
    label: 'GIF → PNG',
    output: 'png',
    hint: 'Useful when you need a still PNG export from a basic GIF image.',
  },
  {
    id: 'bmp-png',
    label: 'BMP → PNG',
    output: 'png',
    hint: 'Helpful when you want a cleaner, more shareable image file.',
  },
];

const universalRouteOptions: ConversionRoute[] = [
  {
    id: 'jpg-png',
    label: 'JPG → PNG',
    output: 'png',
    hint: 'Good when you want a cleaner export for screenshots or graphics.',
  },
  {
    id: 'jpg-webp',
    label: 'JPG → WebP',
    output: 'webp',
    hint: 'Useful when you want smaller web-ready files.',
  },
  {
    id: 'png-jpg',
    label: 'PNG → JPG',
    output: 'jpeg',
    hint: 'Better when transparency is not needed and file size matters more.',
  },
  {
    id: 'png-webp',
    label: 'PNG → WebP',
    output: 'webp',
    hint: 'A good route for lighter web delivery.',
  },
  {
    id: 'webp-png',
    label: 'WebP → PNG',
    output: 'png',
    hint: 'Helpful when another app does not handle WebP well.',
  },
  {
    id: 'webp-jpg',
    label: 'WebP → JPG',
    output: 'jpeg',
    hint: 'Useful for compatibility with older sites and uploads.',
  },
  {
    id: 'gif-png',
    label: 'GIF → PNG',
    output: 'png',
    hint: 'Best for turning a basic still GIF into a clean PNG export.',
  },
  {
    id: 'bmp-png',
    label: 'BMP → PNG',
    output: 'png',
    hint: 'Good when you want a cleaner, more shareable version of a bitmap file.',
  },
];

// JFIF is JPEG with a different extension; browsers decode it natively. Its routes are
// only offered on the JFIF pages so the other converters keep their button sets.
const jfifRoutes: ConversionRoute[] = [
  {
    id: 'jfif-jpg',
    label: 'JFIF → JPG',
    output: 'jpeg',
    hint: 'Same JPEG data, saved as a .jpg that every app and upload form accepts.',
  },
  {
    id: 'jfif-png',
    label: 'JFIF → PNG',
    output: 'png',
    hint: 'Lossless PNG, for editing or when a site asks for PNG.',
  },
  ...universalRouteOptions,
];

export type BrowserImageConverterVariant =
  | 'tiff-to-jpg'
  | 'tiff-to-png'
  | 'jpg-to-tiff'
  | 'png-to-tiff'
  | 'heic-to-png'
  | 'heic-to-webp'
  | 'jfif-to-webp'
  | 'jfif-to-jpg'
  | 'jfif-to-png'
  | 'png-converter'
  | 'image-conversion'
  | 'webp-to-png'
  | 'webp-to-jpg'
  | 'png-to-jpg'
  | 'jpg-to-png'
  | 'png-to-webp'
  | 'jpg-to-webp';

// Lead the route buttons with the page's primary conversion so the default
// route sits first, while still letting people switch to any other route.
function leadWith(primaryRouteId: string): ConversionRoute[] {
  const primary = universalRouteOptions.find((route) => route.id === primaryRouteId);
  if (!primary) {
    return universalRouteOptions;
  }
  return [primary, ...universalRouteOptions.filter((route) => route.id !== primaryRouteId)];
}

// Format-pair pages (roadmap wave 2027-02): the page's conversion leads, every other output
// stays one click away. TIFF and HEIC inputs go through the software decoder.
const OUTPUT_LABEL: Record<OutputFormat, string> = {
  png: 'PNG',
  jpeg: 'JPG',
  webp: 'WebP',
  bmp: 'BMP',
  tiff: 'TIFF',
};

function pairCopy(slug: string, from: string, output: OutputFormat, panelBody: string) {
  const to = OUTPUT_LABEL[output];
  // No "TIFF → TIFF": drop the target and the source's own format from the alternatives.
  const sourceFormat = (
    { JPG: 'jpeg', PNG: 'png', TIFF: 'tiff', WebP: 'webp' } as Record<string, OutputFormat>
  )[from];
  const others = (['jpeg', 'png', 'webp', 'tiff', 'bmp'] as OutputFormat[]).filter(
    (o) => o !== output && o !== sourceFormat,
  );
  const routes: ConversionRoute[] = [
    { id: `${slug}-main`, label: `${from} → ${to}`, output, hint: panelBody },
    ...others.map((o) => ({
      id: `${slug}-${o}`,
      label: `${from} → ${OUTPUT_LABEL[o]}`,
      output: o,
      hint: `Export as ${OUTPUT_LABEL[o]} instead.`,
    })),
  ];
  return {
    toolSlug: slug,
    title: `${from} to ${to} in your browser`,
    description: `Drop in a ${from} file and download a ${to} right away. Nothing is uploaded.`,
    badge: 'Client-side',
    panelTitle: `${from} → ${to}`,
    panelBody,
    supportedCopy: `Upload ${from} (or PNG, JPG, WebP, GIF, BMP, TIFF, HEIC) and export to JPG, PNG, WebP, TIFF or BMP.`,
    emptyHint: `Drop a ${from} file here to convert it to ${to}`,
    routes,
    chips: [from, to, 'No upload'],
    defaultRouteId: `${slug}-main`,
    defaultOutput: output,
  };
}

const converterCopy = {
  'tiff-to-jpg': pairCopy(
    'tiff-to-jpg',
    'TIFF',
    'jpeg',
    'A TIFF scan or export becomes a JPG small enough to email and upload anywhere.',
  ),
  'tiff-to-png': pairCopy(
    'tiff-to-png',
    'TIFF',
    'png',
    'PNG keeps every pixel of the TIFF, transparency included, in a format browsers display.',
  ),
  'jpg-to-tiff': pairCopy(
    'jpg-to-tiff',
    'JPG',
    'tiff',
    'TIFF with lossless LZW compression, the format print shops and archives often ask for.',
  ),
  'png-to-tiff': pairCopy(
    'png-to-tiff',
    'PNG',
    'tiff',
    'TIFF with lossless LZW compression, keeping transparency, for print and archive workflows.',
  ),
  'heic-to-png': pairCopy(
    'heic-to-png',
    'HEIC',
    'png',
    'An iPhone HEIC photo becomes a lossless PNG that opens on Windows, Android and the web.',
  ),
  'heic-to-webp': pairCopy(
    'heic-to-webp',
    'HEIC',
    'webp',
    'An iPhone HEIC photo becomes a compact WebP ready for a website.',
  ),
  'jfif-to-webp': pairCopy(
    'jfif-to-webp',
    'JFIF',
    'webp',
    'A .jfif (JPEG) image becomes a smaller WebP for the web.',
  ),
  'png-converter': {
    toolSlug: 'png-converter',
    title: 'Fast browser conversion',
    description: 'Drop in a file, choose the output, and download it right away.',
    badge: 'Client-side',
    panelTitle: 'Convert PNG both ways',
    panelBody: 'Good for screenshots, graphics, and cleaner edges.',
    supportedCopy: 'Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to PNG, JPG, WebP, or BMP.',
    emptyHint: 'Works best with PNG, JPG, WebP, GIF, BMP, and other browser-friendly image files',
    routes: pngRouteOptions,
    chips: ['PNG', 'JPG', 'WebP', 'GIF', 'BMP'],
    defaultRouteId: 'jpg-png',
    defaultOutput: 'png' as OutputFormat,
  },
  'image-conversion': {
    toolSlug: 'image-conversion',
    title: 'Convert common image formats',
    description: 'Upload one image, choose the export format, and download it right away.',
    badge: 'Browser-first',
    panelTitle: 'Universal image conversion',
    panelBody: 'Best for everyday format changes between the image types browsers handle well.',
    supportedCopy: 'Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to PNG, JPG, WebP, or BMP.',
    emptyHint: 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files',
    routes: universalRouteOptions,
    chips: ['PNG', 'JPG', 'WebP', 'GIF', 'BMP'],
    defaultRouteId: 'jpg-png',
    defaultOutput: 'png' as OutputFormat,
  },
  'jfif-to-jpg': {
    toolSlug: 'jfif-to-jpg',
    title: 'JFIF to JPG in your browser',
    description: 'Drop in a .jfif file and download a standard .jpg right away.',
    badge: 'Client-side',
    panelTitle: 'JFIF → JPG',
    panelBody:
      'A .jfif file is already JPEG data; saving it as .jpg makes upload forms and apps accept it.',
    supportedCopy:
      'Upload JFIF (or JPG, PNG, WebP, GIF, BMP) and export to JPG, PNG, WebP, or BMP.',
    emptyHint: 'Drop a .jfif file here to convert it to JPG',
    routes: jfifRoutes,
    chips: ['JFIF', 'JPG', 'Compatible', 'No upload'],
    defaultRouteId: 'jfif-jpg',
    defaultOutput: 'jpeg' as OutputFormat,
  },
  'jfif-to-png': {
    toolSlug: 'jfif-to-png',
    title: 'JFIF to PNG in your browser',
    description: 'Drop in a .jfif file and download a lossless PNG right away.',
    badge: 'Client-side',
    panelTitle: 'JFIF → PNG',
    panelBody: 'PNG is lossless, so further edits do not add JPEG artifacts.',
    supportedCopy:
      'Upload JFIF (or JPG, PNG, WebP, GIF, BMP) and export to PNG, JPG, WebP, or BMP.',
    emptyHint: 'Drop a .jfif file here to convert it to PNG',
    routes: [jfifRoutes[1], jfifRoutes[0], ...universalRouteOptions],
    chips: ['JFIF', 'PNG', 'Lossless', 'No upload'],
    defaultRouteId: 'jfif-png',
    defaultOutput: 'png' as OutputFormat,
  },
  'webp-to-png': {
    toolSlug: 'webp-to-png',
    title: 'WebP to PNG in your browser',
    description: 'Drop in a WebP file and download a clean, lossless PNG right away.',
    badge: 'Client-side',
    panelTitle: 'WebP → PNG',
    panelBody: 'PNG keeps transparency and sharp edges, so it works everywhere WebP is rejected.',
    supportedCopy: 'Upload WebP (or PNG, JPG, GIF, BMP) and export to PNG, JPG, WebP, or BMP.',
    emptyHint: 'Drop a .webp file here to convert it to PNG',
    routes: leadWith('webp-png'),
    chips: ['WebP', 'PNG', 'Lossless', 'Transparency'],
    defaultRouteId: 'webp-png',
    defaultOutput: 'png' as OutputFormat,
  },
  'webp-to-jpg': {
    toolSlug: 'webp-to-jpg',
    title: 'WebP to JPG in your browser',
    description: 'Drop in a WebP file and download a widely compatible JPG right away.',
    badge: 'Client-side',
    panelTitle: 'WebP → JPG',
    panelBody:
      'JPG is accepted by almost every site, upload form, and older app that rejects WebP.',
    supportedCopy: 'Upload WebP (or PNG, JPG, GIF, BMP) and export to JPG, PNG, WebP, or BMP.',
    emptyHint: 'Drop a .webp file here to convert it to JPG',
    routes: leadWith('webp-jpg'),
    chips: ['WebP', 'JPG', 'Compatible', 'Smaller'],
    defaultRouteId: 'webp-jpg',
    defaultOutput: 'jpeg' as OutputFormat,
  },
  'png-to-jpg': {
    toolSlug: 'png-to-jpg',
    title: 'PNG to JPG in your browser',
    description: 'Drop in a PNG and download a lighter JPG when transparency is not needed.',
    badge: 'Client-side',
    panelTitle: 'PNG → JPG',
    panelBody: 'JPG is smaller and photo-friendly, ideal when the PNG has no transparency to keep.',
    supportedCopy: 'Upload PNG (or JPG, WebP, GIF, BMP) and export to JPG, PNG, WebP, or BMP.',
    emptyHint: 'Drop a .png file here to convert it to JPG',
    routes: leadWith('png-jpg'),
    chips: ['PNG', 'JPG', 'Smaller', 'Photos'],
    defaultRouteId: 'png-jpg',
    defaultOutput: 'jpeg' as OutputFormat,
  },
  'jpg-to-png': {
    toolSlug: 'jpg-to-png',
    title: 'JPG to PNG in your browser',
    description: 'Drop in a JPG and download a clean PNG for screenshots, graphics, and edits.',
    badge: 'Client-side',
    panelTitle: 'JPG → PNG',
    panelBody: 'PNG gives lossless, sharp edges, which is better for text, logos, and screenshots.',
    supportedCopy: 'Upload JPG (or PNG, WebP, GIF, BMP) and export to PNG, JPG, WebP, or BMP.',
    emptyHint: 'Drop a .jpg file here to convert it to PNG',
    routes: leadWith('jpg-png'),
    chips: ['JPG', 'PNG', 'Lossless', 'Graphics'],
    defaultRouteId: 'jpg-png',
    defaultOutput: 'png' as OutputFormat,
  },
  'png-to-webp': {
    toolSlug: 'png-to-webp',
    title: 'PNG to WebP in your browser',
    description: 'Drop in a PNG and download a lighter WebP for faster web delivery.',
    badge: 'Client-side',
    panelTitle: 'PNG → WebP',
    panelBody: 'WebP keeps transparency at a much smaller size, which is great for the web.',
    supportedCopy: 'Upload PNG (or JPG, WebP, GIF, BMP) and export to WebP, PNG, JPG, or BMP.',
    emptyHint: 'Drop a .png file here to convert it to WebP',
    routes: leadWith('png-webp'),
    chips: ['PNG', 'WebP', 'Smaller', 'Web-ready'],
    defaultRouteId: 'png-webp',
    defaultOutput: 'webp' as OutputFormat,
  },
  'jpg-to-webp': {
    toolSlug: 'jpg-to-webp',
    title: 'JPG to WebP in your browser',
    description: 'Drop in a JPG and download a lighter WebP for faster-loading pages.',
    badge: 'Client-side',
    panelTitle: 'JPG → WebP',
    panelBody: 'WebP usually saves noticeable weight over JPG at a similar visual quality.',
    supportedCopy: 'Upload JPG (or PNG, WebP, GIF, BMP) and export to WebP, PNG, JPG, or BMP.',
    emptyHint: 'Drop a .jpg file here to convert it to WebP',
    routes: leadWith('jpg-webp'),
    chips: ['JPG', 'WebP', 'Smaller', 'Web-ready'],
    defaultRouteId: 'jpg-webp',
    defaultOutput: 'webp' as OutputFormat,
  },
} satisfies Record<
  BrowserImageConverterVariant,
  {
    toolSlug: string;
    title: string;
    description: string;
    badge: string;
    panelTitle: string;
    panelBody: string;
    supportedCopy: string;
    emptyHint: string;
    routes: ConversionRoute[];
    chips: string[];
    defaultRouteId: string;
    defaultOutput: OutputFormat;
  }
>;

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(file: File | null) {
  if (!file?.type) {
    return 'Auto';
  }

  const label = file.type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function canvasToBmpBlob(canvas: HTMLCanvasElement) {
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare a BMP export.');
  }

  const { width, height } = canvas;
  const imageData = context.getImageData(0, 0, width, height);
  const rowStride = width * 3;
  const rowPadding = (4 - (rowStride % 4)) % 4;
  const pixelArraySize = (rowStride + rowPadding) * height;
  const fileSize = 54 + pixelArraySize;
  const bytes = new Uint8Array(fileSize);
  const view = new DataView(bytes.buffer);

  view.setUint8(0, 0x42);
  view.setUint8(1, 0x4d);
  view.setUint32(2, fileSize, true);
  view.setUint32(10, 54, true);
  view.setUint32(14, 40, true);
  view.setInt32(18, width, true);
  view.setInt32(22, height, true);
  view.setUint16(26, 1, true);
  view.setUint16(28, 24, true);
  view.setUint32(34, pixelArraySize, true);
  view.setInt32(38, 2835, true);
  view.setInt32(42, 2835, true);

  let offset = 54;

  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = 0; x < width; x += 1) {
      const pixelOffset = (y * width + x) * 4;
      bytes[offset] = imageData.data[pixelOffset + 2];
      bytes[offset + 1] = imageData.data[pixelOffset + 1];
      bytes[offset + 2] = imageData.data[pixelOffset];
      offset += 3;
    }

    offset += rowPadding;
  }

  return new Blob([bytes], { type: 'image/bmp' });
}

type LoadedImage = { source: CanvasImageSource; width: number; height: number };

async function loadImageFromFile(file: File): Promise<LoadedImage> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return { source: image, width: image.naturalWidth, height: image.naturalHeight };
  } catch (error) {
    if (!SOFTWARE_DECODED.test(file.name)) throw error;
    try {
      const canvas = await decodeToCanvas(file);
      return { source: canvas, width: canvas.width, height: canvas.height };
    } catch {
      throw new Error(UNDECODABLE_MESSAGE);
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function BrowserImageConverter({
  variant = 'png-converter',
}: {
  variant?: BrowserImageConverterVariant;
}) {
  const config = converterCopy[variant];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(config.defaultOutput);
  const [quality, setQuality] = useState(0.92);
  const [isDragActive, setIsDragActive] = useState(false);
  const [sourceMeta, setSourceMeta] = useState<SourceMeta | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [convertedSize, setConvertedSize] = useState<number | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [routeHint, setRouteHint] = useState(
    config.routes.find((route) => route.id === config.defaultRouteId)?.hint ?? config.panelBody,
  );
  const [activeRouteId, setActiveRouteId] = useState(config.defaultRouteId);

  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl, previewUrl]);

  function resetOutputState() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setPreviewUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setConvertedSize(null);
  }

  async function inspectSelectedFile(file: File) {
    try {
      const image = await loadImageFromFile(file);
      setSourceMeta({
        width: image.width,
        height: image.height,
      });
    } catch {
      setSourceMeta(null);
    }
  }

  function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setSelectedFile(file);
    setSourceMeta(null);

    if (file) {
      void inspectSelectedFile(file);
    }
  }

  const sourceLabel = normalizeSourceLabel(selectedFile);

  async function runConversion() {
    if (!selectedFile) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsConverting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: config.toolSlug }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            limit?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another conversion right now.';
        throw new Error(message);
      }

      const image = await loadImageFromFile(selectedFile);
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not start image conversion.');
      }

      context.drawImage(image.source, 0, 0);

      const blob =
        selectedOutput.value === 'tiff'
          ? await encodeCanvasInSoftware(canvas, 'tiff')
          : selectedOutput.value === 'bmp'
            ? canvasToBmpBlob(canvas)
            : await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(
                  (nextBlob) => resolve(nextBlob),
                  selectedOutput.mimeType,
                  selectedOutput.qualityEnabled ? quality : undefined,
                );
              });

      if (!blob) {
        throw new Error('The conversion could not finish for this file.');
      }

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextPreviewUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(selectedFile.name)}.${selectedOutput.extension}`;

      setDownloadUrl(nextDownloadUrl);
      setPreviewUrl(nextPreviewUrl);
      setDownloadName(nextFileName);
      setConvertedSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The image could not be converted.');
    } finally {
      setIsConverting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{config.title}</h2>
          <p>{config.description}</p>
        </div>
        <span className="tool-live-badge">{config.badge}</span>
      </div>

      <div className="tool-live-routes" aria-label="Popular conversion routes">
        {config.routes.map((route) => (
          <button
            key={route.label}
            className={`tool-live-route ${activeRouteId === route.id ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setOutputFormat(route.output);
              setRouteHint(route.hint);
              setActiveRouteId(route.id);
              resetOutputState();
            }}
            type="button"
          >
            {route.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {selectedFile ? selectedFile.name : 'Choose an image or drop it here'}
              </strong>
              <span>
                {selectedFile
                  ? `${formatFileSize(selectedFile.size)} • ${selectedFile.type || 'Image file'}`
                  : config.emptyHint}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*,.jfif,.jfi,.jpe,.tif,.tiff,.heic,.heif,.avif"
            className="tool-live-file-input"
            onChange={(event) => handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>From</span>
              <input
                disabled
                readOnly
                type="text"
                value={selectedFile ? sourceLabel : 'Auto-detect after upload'}
              />
            </label>
            <label className="field">
              <span>Convert to</span>
              <select
                value={outputFormat}
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  const nextOption = outputOptions.find(
                    (option) => option.value === event.target.value,
                  );
                  if (nextOption) {
                    setRouteHint(
                      nextOption.value === 'png'
                        ? 'Good for screenshots, graphics, and cleaner edges.'
                        : nextOption.value === 'jpeg'
                          ? 'Better when file size matters more than transparency.'
                          : nextOption.value === 'webp'
                            ? 'Useful for smaller web-ready images.'
                            : 'Useful for older bitmap-friendly workflows.',
                    );
                  }
                  setActiveRouteId(`manual-${event.target.value}`);
                  resetOutputState();
                }}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-quality-shell">
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.4"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.02"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `Used for ${selectedOutput.label} exports`
                  : selectedOutput.value === 'bmp'
                    ? 'BMP exports keep the simple bitmap format without a quality slider'
                    : 'PNG keeps the clean export without a quality slider'}
              </small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!selectedFile || isConverting}
              onClick={() => void runConversion()}
              type="button"
            >
              {isConverting ? 'Converting…' : `Convert to ${selectedOutput.label}`}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download {selectedOutput.label}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>{config.panelTitle}</h3>
          <div className="tool-mockup-chip-list">
            {config.chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
          <p>{routeHint || config.panelBody}</p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>{config.supportedCopy}</span>
          </div>
          {selectedFile ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(selectedFile.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{sourceLabel}</span>
              </div>
              {sourceMeta ? (
                <div>
                  <strong>Resolution</strong>
                  <span>{`${sourceMeta.width} × ${sourceMeta.height}`}</span>
                </div>
              ) : null}
              {convertedSize ? (
                <div>
                  <strong>Converted</strong>
                  <span>{formatFileSize(convertedSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Route</strong>
                <span>{`${sourceLabel} → ${selectedOutput.label}`}</span>
              </div>
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {previewUrl ? (
        <div className="tool-live-preview">
          <h3>Preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="Converted preview" src={previewUrl} />
          </div>
        </div>
      ) : null}
    </article>
  );
}
